/* eslint-disable radix */
import Task from './task';
import TRPCError from './trpc_error.js';
import md5 from '../utils/md5.js';
import getCookie from '../utils/cookie.js';

const TRPC = {};

TRPC.setConfig = function (config) {
  this.version = config.version || '1.0';
  // DUA - demeter UA, 内部用
  this.dua = config.dua || `DV=${this.version}&PL=Web`;
  // 用来生成 Fcgi-Sign
  this.skey = config.skey || getCookie('SKEY');
  // 请求的公共路径
  this.serverUrl = config.serverUrl || '/fcgi';
  // 业务字段
  this.biz = config.biz || 'YZ';
  // RID, 追溯请求用, 通常在本库自动生成
  this.requestID = config.requestID || this.generateUUID();
  this.withCredentials = !!config.withCredentials;
  this.report = config.report;
  // 超时时长
  this.timeout = config.timeout || 8000;
  this.errLogCallback = config.errLogCallback;
  this.sucLogCallback = config.sucLogCallback;
};

TRPC.createRequest = function (options) {
  const timeCST = new Date().getTime();

  const { path } = options;
  const { query } = options;
  const { method } = options;
  const { config } = options;

  this.path = path;
  this.query = query;
  this.method = method;
  this.setConfig(config);

  // 组装请求header
  this.header = this.getRequestHeader(query);
  if (config.header) {
    for (const i in config.header) {
      this.header[i] = config.header[i];
    }
  }

  const params = {
    requestURL: `${this.serverUrl}/${path}`,
    requestHeader: this.header,
    requestMethod: this.method,
    requestID: this.requestID,
    query,
    withCredentials: this.withCredentials,
    timeout: this.timeout,
  };

  return new Promise((resolve, reject) => {
    const task = new Task(params);
    task.sendRequest(
      (res) => {
        resolve(res);

        const sucTrpc = TRPC.getTRPCInfo(res, timeCST);
        // 请求成功的情况下也会发送日志
        if (!params.requestURL.includes('logservice') && this.sucLogCallback && this.sucLogCallback instanceof Function) {
          this.sucLogCallback({
            model: 'network',
            logType: 'interface',
            page: window.location.href,
            total: `${sucTrpc.timeCRT - sucTrpc.timeCST}`,
            args: {
              Path: params.requestURL,
              T0: `${sucTrpc.timeCST}`,
              T1: `${sucTrpc.timeSRT}`,
              T2: `${sucTrpc.timeSST}`,
              T3: `${sucTrpc.timeCRT}`,
              RID: this.requestID,
              Total: `${sucTrpc.timeCRT - sucTrpc.timeCST}`,
              Page: window.location.href,
            },
          });
        }
      },
      (err) => {
        const errTrpc = TRPC.getTRPCError(err, timeCST);
        reject(errTrpc);
        // cls 日志上报时对不同错误的区分处理
        // 不是网络错误, 也不是 FCGI 错误, 则认为是 Error 级别, 否则是 WARN 级别.
        // https://iwiki.woa.com/pages/viewpage.action?pageId=691165044
        const isError = ![TRPCError.TRPCErrorType.NETWORK, TRPCError.TRPCErrorType.FCGI].includes(errTrpc.type);

        // 存在 errLogCallback 函数, 则进行错误上报
        if (!params.requestURL.includes('logservice') && this.errLogCallback && this.errLogCallback instanceof Function) {
          const totalTime = errTrpc.timeCRT - errTrpc.timeCST;
          // 当返回值的 statusCode 为 0, 且没有data为空串, header为空对象时, 说明这是一个特殊的异常请求(超时或者主动cancel)
          // 这种情况下, 如果是主动 cancel 的, 则不进行错误上报
          if (
            err
            && err.statusCode === 0
            && err.data === ''
            && err.header
            && err.header[''] === ''
            && totalTime < (this.timeout - 1000)) {
            return;
          }
          this.errLogCallback(
            isError ? 'ERROR' : 'WARN',
            {
              model: 'network',
              logType: 'interface',
              page: window.location.href,
              errCode: `${errTrpc.code}`,
              total: `${totalTime}`,
              args: {
                Path: params.requestURL,
                HttpCode: err.statusCode,
                TrpcCode: errTrpc.code,
                TrpcErrType: errTrpc.type,
                T0: `${errTrpc.timeCST}`,
                T1: `${errTrpc.timeSRT}`,
                T2: `${errTrpc.timeSST}`,
                T3: `${errTrpc.timeCRT}`,
                RID: this.requestID,
                Total: `${totalTime}`,
                Page: window.location.href,
                err: JSON.stringify(err),
              },
            },
          );
        }
        TRPC.reportError(errTrpc);
      },
    );
  });
};

/**
 * 核心方法, 通过此方法发起请求
 * @param {string} path 请求路径
 * @param {object} query 请求参数
 * @param {object} config TRPC 配置
 * @returns Promise
 */
TRPC.newRequest = function (path, query, config = {}) {
  if (arguments.length > 1) {
    return TRPC.createRequest({
      path,
      query,
      config,
    });
  }
  return TRPC.createRequest.apply(TRPC, arguments);
};

/**
 * 从响应中提取错误信息
 * @param {*} res 服务端响应
 * @param {*} timeCST 请求发送时间
 * @returns
 */
TRPC.getTRPCError = function (res, timeCST) {
  const errTrpc = new TRPCError();
  errTrpc.rawData = res;
  errTrpc.timeCST = timeCST;
  errTrpc.timeCRT = new Date().getTime();
  errTrpc.timeSRT = 0;
  errTrpc.timeSST = 0;

  if (res && res.header) {
    const { statusCode } = res;
    const fcgiRet = res.header['Fcgi-Ret'] || res.header['fcgi-ret'];
    const trpcRet = res.header['Trpc-Ret'] || res.header['trpc-ret'];
    errTrpc.timeCRT = new Date().getTime();
    errTrpc.timeSRT = res.header['Fcgi-Srtime'] || res.header['fcgi-srtime'] || '0';
    errTrpc.timeSST = res.header['Fcgi-Sstime'] || res.header['fcgi-sstime'] || '0';
    if (errTrpc.timeSRT) {
      errTrpc.timeSRT = parseInt(errTrpc.timeSRT);
    }
    if (errTrpc.timeSST) {
      errTrpc.timeSST = parseInt(errTrpc.timeSST);
    }
    // 状态码非200, HTTP 错误
    // 状态码 200 的情况下, 检查header中的fcgi和trpc错误码
    if (statusCode !== 200) {
      errTrpc.errInfo = `TRPC ResponseError Http Code erro :${statusCode}`;
      errTrpc.code = statusCode;
      errTrpc.type = TRPCError.TRPCErrorType.HTTP;
    } else if (trpcRet) {
      errTrpc.type = TRPCError.TRPCErrorType.TRPC;
      errTrpc.code = parseInt(trpcRet);
      errTrpc.errInfo = 'TRPC ResponseError TRPC';
    } else if (fcgiRet) {
      errTrpc.type = TRPCError.TRPCErrorType.FCGI;
      errTrpc.code = parseInt(fcgiRet);
      const fcgiError = res.header['Fcgi-Error'] || res.header['fcgi-error'] || '';
      try {
        errTrpc.errInfo = decodeURIComponent(fcgiError);
      } catch (e) {
        errTrpc.errInfo = 'TRPC ResponseError FCGI';
      }
    }
  } else {
    errTrpc.type = TRPCError.TRPCErrorType.NETWORK;
    errTrpc.code = -902;
    errTrpc.errInfo = 'TRPC ResponseError NETWORK';
  }
  errTrpc.totalClient = errTrpc.timeCRT - errTrpc.timeCST;
  errTrpc.totalFcgi = errTrpc.timeSST - errTrpc.timeSRT;

  // 返回错误信息
  // type: 错误类型 HTTP, TRPC, FCGI, NETWORK
  // code: 具体错误码, 由各个业务有划分对应的错误码号段
  // errInfo: 错误信息. 如果是Fcgi错误, 业务会填充 Fcgi-Error 和 Fcgi-Errormsg 字段, 这里取的 Fcgi-Error
  return errTrpc;
};

TRPC.getTRPCInfo = function (res, timeCST) {
  const info = {};
  info.timeCST = timeCST;
  info.timeCRT = new Date().getTime();
  info.timeSRT = 0;
  info.timeSST = 0;
  if (res && res.header) {
    info.timeSRT = res.header['Fcgi-Srtime'] || res.header['fcgi-srtime'] || '0';
    info.timeSST = res.header['Fcgi-Sstime'] || res.header['fcgi-sstime'] || '0';
    if (info.timeSRT) {
      info.timeSRT = parseInt(info.timeSRT);
    }
    if (info.timeSST) {
      info.timeSST = parseInt(info.timeSST);
    }
  }
  info.code = 0;
  return info;
};

// 生成 RequestID的方法
TRPC.generateUUID = function () {
  const len = 36; // 长度为36
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
  const uuid = [];
  let i;

  let r;
  uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-';
  uuid[14] = '4';
  for (i = 0; i < len; i++) {
    if (!uuid[i]) {
      r = 0 | (Math.random() * 16);
      uuid[i] = chars[i === 19 ? (r & 0x3) | 0x8 : r];
    }
  }
  return uuid.join('');
};

TRPC.getValueFromCookie = function (key, str) {
  if (!str) return;
  const cookie = str;

  const m = cookie.match(new RegExp(`(^| |;|,)${key}=([^;]*)(;|$)`));
  console.log(!m ? '' : decodeURIComponent(m[2]));
  return !m ? '' : decodeURIComponent(m[2]);
};

/**
 * 生成标准的 request header
 * @param {object} query 请求参数
 * @returns
 */
TRPC.getRequestHeader = function (query) {
  const {
    requestID,
  } = this;
  const {
    skey,
  } = this;
  const {
    dua,
  } = this;
  const {
    biz,
  } = this;
  const time = new Date().getTime();
  const md_key = md5(skey + time + JSON.stringify(query));

  const header = {};
  header.RequestID = requestID;
  header['D-UA'] = dua;
  header.BIZ = biz;
  header.OriginCSTime = time;
  header['Content-Type'] = 'application/json';
  header['Fcgi-Sign'] = md_key;
  return header;
};

TRPC.reportError = function (error) {
  const { report } = this;
  if (report && typeof report === 'function') {
    report('trpc_request_problem', {
      request_id: this.requestID,
      type: 'error',
      fcgi: this.path,
      CST: error.timeCST,
      SRT: error.timeSRT,
      SST: error.timeSST,
      CRT: error.timeCRT,
      TCC: error.totalClient,
      TFC: error.totalFCgi,
      request_json: JSON.stringify(this.query || ''),
      error_type: error.type,
      error_code: error.code,
      error_msg: error.errInfo,
    });
  }
};

TRPC.TRPCError = TRPCError;

export default TRPC;
